package mapsearch.websocket

import apiclient.FormationClient
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.restGetObjectById
import apiclient.mapsearch.MapFeature
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.UserMarker
import apiclient.websocket.MapMarkerWebsocketModule
import apiclient.websocket.MessageToServer
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import koin.koinCtx
import koin.withKoin
import kotlin.math.min
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.Job
import kotlinx.datetime.Instant
import map.MapStateStore
import maplibreGL.Marker
import mapsearch.ActiveMapTilesStore
import mapsearch.ClusterService
import mapsearch.MapMarkerTileSearchResultsHolder
import mapsearch.MapTileCache
import mapsearch.elementId
import mapsearch.generalMarkers.GeneralMarkerLookUpStore
import mapsearch.generalMarkers.GeneralMarkersSearchSpecStore
import mapsearch.globalsearch.toMapMarker
import mapsearch.historyPathTool.ObjectHistoryResultsCache
import mapsearch.tiles
import mapsearch.updateFromMapMarker
import mapsearch.users.UserMapMarkerStore
import mapsearch.zones.ZoneObjectLookUpStore
import mapsearch.zones.ZoneSearchSpecStore
import model.MapState
import web.console.console

data class WebsocketData(
    val users: Map<String, UserMarker> = emptyMap(),
    val objects: Map<String, MapMarker> = emptyMap()
)

class MapMarkerSubscription : RootStore<WebsocketData>(WebsocketData(), Job()) {

    val mapMarkerWebsocketModule: MapMarkerWebsocketModule = koinCtx.get()
    val mapStateStore: MapStateStore = koinCtx.get()

    var lastWebsocketState: Pair<Map<String, MapFeature>, Instant>? = null

    fun startReceiveWebsocketMapMarkerUpdates() {
        update(WebsocketData())
    }

    private fun handleUserUpdates(userUpdates: List<UserMarker>) {
        withKoin {
            val userMapMarkerStore: UserMapMarkerStore = get()
            userMapMarkerStore.handleUserUpdates(userUpdates)
        }
    }

    fun removeRenderedMarkersFromMapInAllTiles(
        markerIds: Set<String>,
    ): List<Pair<MapMarkerTileSearchResultsHolder, List<Marker>>> {
        withKoin {
            val activeMapTilesStore: ActiveMapTilesStore = get()
            val updatedList = activeMapTilesStore.current.map { (holder, markers) ->
                val updatedMarkers = markers.mapNotNull { marker ->
                    marker.elementId?.let { id ->
                        if (id in markerIds) {
                            // remove marker from map
                            marker.remove()
                        }
                        marker
                    }
                }
                holder to updatedMarkers
            }
            return updatedList
        }
    }

    val refetchAndUpdateGeneralMarker = SimpleHandler<String> { data, _ ->
        data handledBy { objId ->
            try {
                withKoin {
                    val client = get<FormationClient>()
                    val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
                    val activeMapTilesStore: ActiveMapTilesStore = get()
                    val mapTileCache: MapTileCache = get()

                    client.restGetObjectById(objId).fold(
                        { result ->
                            activeMapTilesStore.updateMapMarkersInTiles(
                                tileCache = mapTileCache.cache,
                                newMarkers = listOf(result.toMapMarker()),
                            )
                            generalMarkersSearchSpecStore
                                .addOrUpdateExistingGeneralMarker(result)
                        },
                        { error ->
                            console.error(
                                "Failed to fetch general marker.",
                                error.message,
                                error.cause,
                            )
                        },
                    )
                }
            } catch (e: Exception) {
                console.log(e)
            }
        }
    }

    fun handleObjectDeletions(markers: List<GeoObjectDetails>) {
        withKoin {
            val activeMapTilesStore: ActiveMapTilesStore = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            val zoneObjectLookUpStore: ZoneObjectLookUpStore = get()
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()
            val clusterService: ClusterService = get()

            val markerIds = markers.map { it.id }

            activeMapTilesStore.deleteMapMarkersInTiles(
                deleteMarkerIds = markerIds,
            )

            clusterService.removeFeatures(markerIds)
            zoneSearchSpecStore.removeZonesFromMap(markerIds.toSet())
            zoneObjectLookUpStore.removeZones(markers)
            generalMarkersSearchSpecStore.removeGeneralMarkersFromMap(markerIds.toSet())
            generalMarkerLookUpStore.removeGeneralMarkers(markers)
        }
    }

    fun handleObjectUpdates(
        mapMarkers: List<MapMarker>,
        lastState: Pair<Map<String, MapFeature>, Instant>? = null
    ) {
//        kotlin.js.console.log("^ handleObjectUpdates", mapMarkers.map { "${it.title}, ${it.color}, ${it.position}" }.toString())
        withKoin {
            val mapTileCache: MapTileCache = get()
            val activeMapTilesStore: ActiveMapTilesStore = get()
            val objectHistoryResultsCache: ObjectHistoryResultsCache = get()
            val generalMarkerLookUpStore: GeneralMarkerLookUpStore = get()
            val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            val zoneObjectLookUpStore: ZoneObjectLookUpStore = get()
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()
            val clusterService: ClusterService = get()

            val changedMarkers = lastState?.let { (lastObjects, lastTimeStamp) ->
                if (lastTimeStamp.toEpochMilliseconds() > (Clock.System.now() - 40.seconds).toEpochMilliseconds()) {
                    mapMarkers.filter { marker ->
                        marker != (lastObjects[marker.id] as? MapMarker)
                    }
                } else {
                    lastWebsocketState = null
                    mapMarkers
                }
            } ?: mapMarkers

            changedMarkers.ifEmpty { null }?.let { changed ->
                activeMapTilesStore.updateMapMarkersInTiles(
                    tileCache = mapTileCache.cache,
                    newMarkers = changed,
                )

                // Update history path of markers in path tool
                objectHistoryResultsCache.updateHistoryForObjects(changed)


                val otherFeatures: MutableList<MapMarker> = mutableListOf()
                // find objects in LookUps and update them respectively
                // in clusterFeatures, GeneralMarkerLookUp or ZoneLookUp
                changed.forEach { marker ->
                    if (marker.id in clusterService.current.mapSearchFeatures.mapNotNull { if (it is MapMarker) it.id else null }) {
                        otherFeatures.add(marker)
                    }
                    generalMarkerLookUpStore.current[marker.id]?.let { obj ->
                        generalMarkersSearchSpecStore.addOrUpdateExistingGeneralMarker(
                            obj.updateFromMapMarker(marker),
                        )
                        // after direct update, always re-fetch generalMarkers
                        // to get potentially changed rotation of geoReferencedShape
                        refetchAndUpdateGeneralMarker(obj.id)
                    }
                    zoneObjectLookUpStore.current[marker.id]?.let { obj ->
                        zoneSearchSpecStore.addOrUpdateExistingZone(
                            obj.updateFromMapMarker(marker),
                        )
                    }
                }

                clusterService.updateFeatureMarkers(otherFeatures)

//                console.log("^ Applied changes from Websocket for:", changed.map { "${it.title}, ${it.position}" }.toString())
            }

            // For fun :)
//            changedMarkers.lastOrNull()?.let { marker ->
//                withKoin {
//                    val objectAndUserHandler: ObjectAndUserHandler = get()
//                    objectAndUserHandler.updateAndLocateActiveObjectByExternalIdOrInternalId(marker.id)
//                }
//            }
        }
    }

    fun handleNewObjects(
        newObjects: List<GeoObjectDetails>
    ) {
        withKoin {
            val mapTileCache: MapTileCache = get()
            val activeMapTilesStore: ActiveMapTilesStore = get()
            val objectHistoryResultsCache: ObjectHistoryResultsCache = get()
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()
            val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            val clusterService: ClusterService = get()

            if (newObjects.isNotEmpty()) {

                val newMapMarkers = newObjects.map {
                    it.toMapMarker()
                }

                activeMapTilesStore.addMarkersToTiles(
                    tileCache = mapTileCache.cache,
                    newMarkers = newMapMarkers,
                )

                // Update the history path of markers in the path tool
                objectHistoryResultsCache.updateHistoryForObjects(newMapMarkers)

                val groupedNewObjects = newObjects.groupBy { geoObjectDetails ->
                    when (geoObjectDetails.objectType) {
                        ObjectType.Zone -> geoObjectDetails.objectType
                        ObjectType.GeneralMarker -> geoObjectDetails.objectType
                        else -> null
                    }
                }
                groupedNewObjects[ObjectType.Zone].orEmpty().forEach { zone ->
                    zoneSearchSpecStore.addOrUpdateExistingZone(zone)
                }
                groupedNewObjects[ObjectType.GeneralMarker].orEmpty().forEach { generalMarker ->
                    generalMarkersSearchSpecStore
                        .addOrUpdateExistingGeneralMarker(generalMarker)
                }
                clusterService.updateFeatureMarkers(newMarkers = groupedNewObjects[null].orEmpty().map { it.toMapMarker() })

                console.log(
//                    "^ Added new Features to cache and map:",
                    newObjects.map { it.title }.toString(),
                )
            }
        }
    }

    private suspend fun updateTileSubscription(mapState: MapState?) {
        mapState?.let { state ->
            val (zl, tiles) = state.copy(
                zoom = min(state.zoom, 21.0),
            ).tiles()
//            console.log("Update tile subscription with for zl: $zl with tiles: ${tiles.map { it.path }}")
            mapMarkerWebsocketModule.updateTileSubscription(
                MessageToServer.UpdateSubscription(tiles),
            )
        }
    }

    init {
        console.log("Start syncing mapMarker websocket subscription")
        mapStateStore.data handledBy { newState ->
            // update mapMarker websocket subscription with new tiles
            updateTileSubscription(newState)
        }
        console.log("Start receiving updates via MapMarkerWebsocketModule")
        mapMarkerWebsocketModule.mapMarkers handledBy { stateValue ->
            val (state, timeStamp) = stateValue
//            console.log("^ Websocket update: ${timeStamp.formatSimpleTimestamp()}")
            console.log("^ Websocket objects", state.values.filterIsInstance<MapMarker>().map { it.title }.toString())
            console.log("^ Websocket users", state.values.filterIsInstance<UserMarker>().map { it.userId }.toString())
            handleUserUpdates(state.values.filterIsInstance<UserMarker>().map { it.copy(updatedAt = timeStamp) })
            handleObjectUpdates(state.values.filterIsInstance<MapMarker>().map { it.copy(updatedAt = timeStamp) }, lastWebsocketState)
            lastWebsocketState = stateValue
        }
    }
}
